import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let mapsPromise = null;

// Loads the Maps JS API once (async bootstrap + callback), exposing google.maps.importLibrary.
function loadGoogleMaps() {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  if (mapsPromise) return mapsPromise;
  mapsPromise = (async () => {
    const response = await fetch(`${API_URL}/api/config/google-maps/public-key`);
    const data = await response.json();
    if (!data.api_key) throw new Error('KEY_MISSING');
    await new Promise((resolve, reject) => {
      window.__cimonamiaMapsReady = resolve;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(data.api_key)}&loading=async&v=weekly&callback=__cimonamiaMapsReady`;
      script.async = true;
      script.onerror = () => reject(new Error('LOAD_FAILED'));
      document.head.appendChild(script);
    });
  })().catch((err) => {
    mapsPromise = null;
    throw err;
  });
  return mapsPromise;
}

// Neighbourhood / zone of an address ("Zona 10", "Usaquén"): sent to the courier with the address.
const DISTRICT_TYPES = ['sublocality_level_1', 'sublocality', 'neighborhood'];

// Google splits a hyphenated door number ("12-34") and keeps only the first part as street_number, but the
// formatted address still has it whole ("6A Avenida 12-34, Zona 9…"). Use the whole number when the
// formatted address continues Google's number with hyphen groups.
const wholeHouseNumber = (route, number, formatted) => {
  if (!route || !number || !formatted) return number;
  const first = String(formatted).split(',')[0].trim();
  if (!first.toLowerCase().startsWith(route.toLowerCase())) return number;
  const rest = first.slice(route.length).replace(/^[\s#.-]+/, '');
  return rest.startsWith(number) && rest.length > number.length && /^[0-9A-Za-z]+(?:[-–][0-9A-Za-z]+)+$/.test(rest) ? rest : number;
};

// `country` (ISO-2, e.g. 'US') restricts suggestions to that country at the source
// (PlaceAutocompleteElement.includedRegionCodes) and rejects any place outside it.
const AddressAutocomplete = ({ onAddressSelect, disabled = false, country = null }) => {
  const { t, i18n } = useTranslation();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const elementRef = useRef(null);
  const legacyRef = useRef(null);
  const countryRef = useRef(country);
  const onSelectRef = useRef(onAddressSelect);
  countryRef.current = country;
  onSelectRef.current = onAddressSelect;

  const [mode, setMode] = useState('loading'); // loading | element | legacy | error
  const [error, setError] = useState(null);
  const [wrongCountry, setWrongCountry] = useState(false);

  const emit = (addressData) => {
    const allowed = countryRef.current;
    if (allowed && (addressData.country || '').toUpperCase() !== allowed.toUpperCase()) {
      setWrongCountry(true);
      return;
    }
    setWrongCountry(false);
    onSelectRef.current?.(addressData);
  };

  // New Places API: <gmp-place-autocomplete> fires `gmp-select` with a place prediction.
  const handleElementSelect = async (event) => {
    try {
      const place = event.placePrediction ? event.placePrediction.toPlace() : event.place;
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'location'] });
      const comps = place.addressComponents || [];
      const find = (type) => comps.find((c) => c.types.includes(type));
      const route = find('route')?.longText || '';
      // The clicked suggestion's own text ("6A Avenida 12-34, Zona 9, …") is where Google keeps the whole number.
      const clicked = event.placePrediction?.text?.text || '';
      emit({
        street: route,
        number: wholeHouseNumber(route, wholeHouseNumber(route, find('street_number')?.longText || '', clicked), place.formattedAddress),
        district: DISTRICT_TYPES.map((t) => find(t)?.longText).find(Boolean) || '',
        fullAddress: place.formattedAddress,
        city: find('locality')?.longText || find('administrative_area_level_2')?.longText || '',
        state: find('administrative_area_level_1')?.shortText || '',
        postalCode: find('postal_code')?.longText || '',
        country: find('country')?.shortText || '', // ISO 2-letter (CO, US, ...) required by the backend
        lat: place.location?.lat(),
        lng: place.location?.lng(),
      });
    } catch (err) {
      console.error('Error reading selected place:', err);
      setError('addressMore.autocompleteInitFailed');
    }
  };

  // Legacy widget, only used if the new element isn't available.
  const handleLegacyChanged = () => {
    const place = legacyRef.current.getPlace();
    if (!place || !place.address_components) return;
    const long = (type) => place.address_components.find((c) => c.types.includes(type))?.long_name || '';
    const short = (type) => place.address_components.find((c) => c.types.includes(type))?.short_name || '';
    emit({
      street: long('route'),
      number: wholeHouseNumber(long('route'), long('street_number'), place.formatted_address),
      district: DISTRICT_TYPES.map((t) => long(t)).find(Boolean) || '',
      fullAddress: place.formatted_address,
      city: long('locality') || long('administrative_area_level_2'),
      state: short('administrative_area_level_1'),
      postalCode: long('postal_code'),
      country: short('country'),
      lat: place.geometry?.location?.lat(),
      lng: place.geometry?.location?.lng(),
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadGoogleMaps();
        const places = await window.google.maps.importLibrary('places');
        if (cancelled) return;

        if (places.PlaceAutocompleteElement && containerRef.current) {
          const options = countryRef.current ? { includedRegionCodes: [countryRef.current.toLowerCase()] } : {};
          const el = new places.PlaceAutocompleteElement(options);
          try { el.placeholder = t('addressMore.startTypingAddress'); } catch (e) { /* older API versions */ }
          el.style.display = 'block';
          el.style.width = '100%';
          el.style.colorScheme = 'light';
          el.addEventListener('gmp-select', handleElementSelect);
          el.addEventListener('gmp-placeselect', handleElementSelect);
          containerRef.current.appendChild(el);
          elementRef.current = el;
          setMode('element');
        } else if (places.Autocomplete && inputRef.current) {
          legacyRef.current = new places.Autocomplete(inputRef.current, {
            types: ['address'],
            fields: ['address_components', 'formatted_address', 'geometry'],
            ...(countryRef.current ? { componentRestrictions: { country: countryRef.current.toLowerCase() } } : {}),
          });
          legacyRef.current.addListener('place_changed', handleLegacyChanged);
          setMode('legacy');
        } else {
          throw new Error('PLACES_UNAVAILABLE');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading Google Maps:', err);
        setError(err.message === 'KEY_MISSING' ? 'addressMore.mapsKeyMissing' : 'addressMore.mapsLoadFailed');
        setMode('error');
      }
    })();
    return () => {
      cancelled = true;
      if (elementRef.current) {
        elementRef.current.remove();
        elementRef.current = null;
      }
    };
  }, []);

  // Keep the restriction in sync when the storefront country changes.
  useEffect(() => {
    setWrongCountry(false);
    if (!country) return;
    if (elementRef.current) elementRef.current.includedRegionCodes = [country.toLowerCase()];
    if (legacyRef.current) legacyRef.current.setComponentRestrictions({ country: country.toLowerCase() });
  }, [country]);

  useEffect(() => {
    if (elementRef.current) {
      elementRef.current.style.pointerEvents = disabled ? 'none' : '';
      elementRef.current.style.opacity = disabled ? '0.6' : '';
    }
  }, [disabled, mode]);

  const countryName = (() => {
    try { return new Intl.DisplayNames([i18n.language || 'en'], { type: 'region' }).of((country || '').toUpperCase()) || country; }
    catch (e) { return country; }
  })();

  if (mode === 'error') {
    return (
      <div>
        <Input placeholder={t('addressMore.enterManually')} disabled />
        <p className="text-xs text-red-500 mt-1">{t(error)}</p>
      </div>
    );
  }

  const loading = mode === 'loading';

  return (
    <div className="relative">
      <div ref={containerRef} data-testid="address-autocomplete-element" className={mode === 'element' ? '' : 'hidden'} />
      {mode !== 'element' && (
        <Input
          ref={inputRef}
          type="text"
          placeholder={loading ? t('addressMore.loadingMaps') : t('addressMore.startTypingAddress')}
          disabled={disabled || loading}
          autoComplete="off"
          className="w-full"
        />
      )}
      {loading && (
        <p className="text-xs text-gray-500 mt-1">⏳ {t('addressMore.loadingAutocomplete')}</p>
      )}
      {wrongCountry && (
        <p className="text-xs text-red-600 mt-1" role="alert">
          {t('addressMore.wrongCountry', { country: countryName })}
        </p>
      )}
      {!loading && !wrongCountry && (
        <p className="text-xs text-gray-500 mt-1">
          💡 {country ? t('addressMore.startTypingInCountry', { country: countryName }) : t('addressMore.startTypingSuggestions')}
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
